<template>
  <admin-page-shell
    :title="$t('frontend_groovemail_smtp_settings')"
    :subtitle="$t('frontend_groovemail_smtp_configure_setting')"
  >
    <v-wait for="settingdetail">

      <!-- Loader -->
      <portal to="loaderTarget">
        <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
          <gd-loader :label="$t('frontend_loading')"/>
        </div>
      </portal>

      <div class="gm-admin-tab__stack gm-admin-form">
        <gs-field :label="$t('frontend_groovemail_smtp_pool_level')">
          <select
            v-model="model.pool_type"
            @change="changePoolType($event)"
          >
            <option value="">
              {{ $t('frontend_groovemail_smtp_select_pool_level') }}
            </option>
            <option
              v-for="poolData in this.poolData"
              :key="poolData.id"
              :value="poolData.id"
            >
              {{ poolData.name }}
            </option>
          </select>
          <p
            v-if="this.formSubmitted && !model.pool_type"
            class="gm-admin-tab__error"
          >
            {{ $t('frontend_groovemail_smtp_select_pool_level_required') }}
          </p>
        </gs-field>

        <gs-field :label="$t('frontend_groovemail_smtp_tier_level')">
          <select v-model="model.tier_type">
            <option value="">
              {{ $t('frontend_groovemail_smtp_select_tier_level') }}
            </option>
            <option
              v-for="tierData in this.tierData"
              :key="tierData.id"
              :value="tierData.id"
            >
              {{ tierData.name }}
            </option>
          </select>
          <p
            v-if="this.formSubmitted && !model.tier_type"
            class="gm-admin-tab__error"
          >
            {{ $t('frontend_groovemail_smtp_select_tier_level_required') }}
          </p>
        </gs-field>

        <gs-field :label="$t('frontend_groovemail_smtp_name')">
          <text-input
            variant="base"
            v-model="model.smtp_name"
            :placeholder="this.$t('frontend_groovemail_admin_enter_smtp_name')"
            :validator="$v.model.smtp_name"
          />
        </gs-field>

        <gs-field :label="$t('frontend_groovemail_smtp_mail_hots')">
          <text-input
            variant="base"
            v-model="model.mail_host"
            :placeholder="this.$t('frontend_groovemail_smtp_enter_mail_host')"
            :validator="$v.model.mail_host"
          />
        </gs-field>

        <gs-field :label="$t('frontend_groovemail_smtp_mail_port')">
          <text-input
            variant="base"
            v-model="model.mail_port"
            :placeholder="this.$t('frontend_groovemail_admin_enter_mail_port')"
            :validator="$v.model.mail_port"
          />
        </gs-field>

        <gs-field :label="$t('frontend_groovemail_smtp_mail_encryption')">
          <text-input
            variant="base"
            v-model="model.mail_enc"
            :placeholder="this.$t('frontend_groovemail_admin_enter_mail_encryption')"
          />
        </gs-field>

        <gs-field :label="$t('frontend_groovemail_smtp_mail_username')">
          <text-input
            variant="base"
            v-model="model.mail_username"
            :placeholder="this.$t('frontend_groovemail_admin_enter_mail_username')"
            :validator="$v.model.mail_username"
          />
        </gs-field>

        <gs-field :label="$t('frontend_groovemail_smtp_mail_password')">
          <password-input
            :class="{
              'border-danger':
                this.formSubmitted &&
                this.$v.model.mail_password.$invalid,
            }"
            :validator="$v.model.mail_password"
            :attribute="
              this.$t('frontend_groovemail_smtp_mail_password')
            "
            v-model="model.mail_password"
            value="model.mail_password"
            :placeholder="
              this.$t('frontend_groovemail_admin_enter_mail_password')
            "
          />
          <p
            v-if="
              this.formSubmitted &&
              this.$v.model.mail_password.$invalid
            "
            class="gm-admin-tab__error"
          >
            {{ $t('frontend_groovemail_smtp_mail_password_field') }}
          </p>
        </gs-field>
      </div>

      <div class="gm-admin-page__footer">
        <gs-button variant="primary" @click="createSetting()">
          {{ this.$route.params.id ? $t('frontend_update') : $t('frontend_save') }}
        </gs-button>
      </div>
    </v-wait>
  </admin-page-shell>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import { adminUrls } from '@/utils/urls.js';
import { getData, postData, putData } from '@/utils/apiHelper.js';
import AdminPageShell from '../AdminPageShell.vue';

export default {
  components: {
    AdminPageShell,
  },
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_smtp_settings') };
  },

  data() {
    return {
      model: {
        smtp_name: '',
        mail_host: '',
        mail_port: '',
        mail_enc: '',
        mail_username: '',
        mail_password: '',
        pool_type: '',
        tier_type: '',
      },
      isLoading: false,
      formSubmitted: false,
      poolData: [],
      tierData: [],
    };
  },

  validations: {
    model: {
      smtp_name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      mail_host: {
        required,
      },
      mail_port: {
        required,
      },
      mail_username: {
        required,
      },
      mail_password: {
        required,
      },
    },
  },
  methods: {
    /**
     * Fetches tier filter data based on pool filter type selected.
     * @param {object} event contains selected pool fiter data.
     */
    async changePoolType(event) {
      this.model.tier_type = '';
      if (event.target.value) {
        const smtpTierTypeRequestData = {
          pool_id: event.target.value,
        };
        await this.getTierData(smtpTierTypeRequestData);
      } else {
        this.tierData = [];
      }
    },

    // Saves smtp settings & redirects user to admin smtp listing page.
    async createSetting() {
      this.$v.model.smtp_name.$touch();
      this.$v.model.mail_port.$touch();
      this.$v.model.mail_username.$touch();
      this.$v.model.mail_password.$touch();
      this.$v.model.mail_host.$touch();
      this.formSubmitted = true;

      if (
        !this.$v.model.smtp_name.$invalid &&
        !this.$v.model.mail_port.$invalid &&
        !this.$v.model.mail_host.$invalid &&
        !this.$v.model.mail_username.$invalid &&
        !this.$v.model.mail_password.$invalid &&
        this.model.pool_type &&
        this.model.tier_type
      ) {
        this.adminsmtp.smtp_name = this.model.smtp_name;
        this.adminsmtp.mail_host = this.model.mail_host;
        this.adminsmtp.mail_port = this.model.mail_port;
        this.adminsmtp.mail_enc = this.model.mail_enc;
        this.adminsmtp.mail_username = this.model.mail_username;
        this.adminsmtp.mail_password = this.model.mail_password;
        this.adminsmtp.pool_type_id = this.model.pool_type;
        this.adminsmtp.smtp_type_id = this.model.tier_type;
        if (this.$route.params.id) {
          await putData(`${adminUrls.smtpUrl.smtpData}/${this.$route.params.id}`, this.adminsmtp);
        } else {
          await postData(adminUrls.smtpUrl.smtpData, this.adminsmtp);
        }
        this.$router.push({ name: 'AdminSmtp' });
      }
    },

    // Fetches pool data
    async getPoolData() {
      const poolData = await getData(
        adminUrls.smtpUrl.smtpPoolTypes,
        null,
        true
      );
      this.poolData = poolData && poolData.data;
    },

    /**
     * Fetches tier filter data.
     * @param {object} reqPayload contains pool id that's required for fetching tier data.
     * @param {boolean} primaryLoading variant="primary" v-wait loader spinning or not
     *
     */
    async getTierData(reqPayload, primaryLoading) {
      const tierData = await postData(
        adminUrls.smtpUrl.smtpTierTypes,
        reqPayload,
        null,
        primaryLoading
      );
      this.tierData = tierData.data;
    },

    // Creates new Smtp object or fetch existing if id passed as route params.
    async fetchSmtpData() {
      try {
        if (this.$route.params.id === undefined) {
          this.adminsmtp = {};
        } else {
          this.$wait.start('settingdetail');
          const smtpData = await getData(
            `${adminUrls.smtpUrl.smtpData}/${this.$route.params.id}`,
            null,
            true
          );
          this.adminsmtp = smtpData.data;
          this.model.smtp_name = this.adminsmtp.smtp_name;
          this.model.mail_host = this.adminsmtp.mail_host;
          this.model.mail_port = this.adminsmtp.mail_port;
          this.model.mail_enc = this.adminsmtp.mail_enc;
          this.model.mail_username = this.adminsmtp.mail_username;
          this.model.mail_password = this.adminsmtp.mail_password;
          this.model.pool_type = this.adminsmtp.pool_type_id;
          this.model.tier_type = this.adminsmtp.smtp_type_id;

          if (this.adminsmtp.pool_type_id && this.adminsmtp.smtp_type_id) {
            const smtpTierTypeRequestData = {
              pool_id: this.adminsmtp.pool_type_id,
            };
            await this.getTierData(smtpTierTypeRequestData, true);
          }
        }
        this.$wait.end('settingdetail');
      } catch (e) {
        this.$wait.end('settingdetail');
      }
    },

  },

  // Fetch & initialize Smtp data
  async created() {
    // show loader
    this.isLoading = true;

    await Promise.all([this.getPoolData(), this.fetchSmtpData()]);

    // hide loader
    this.isLoading = false;
  },
};
</script>

